<script setup lang="ts">
import HSForm from '~/components/forms/HSForm.vue'
import TheLoader from '~/components/TheLoader.vue'
import { createElementFromString } from '~/utils/string'
import CheckCircle from '~/assets/icons/check-circle.svg?raw'
import { twMerge } from 'tailwind-merge'

defineProps<{
  formId: string
  title: string
  image: string
  withOverlay?: boolean
  imageExtraClasses?: string
}>()

const isFormSubmitted = ref(false)
const hubspotUserSubmittedData = reactive({
  firstName: '',
  lastName: '',
  email: '',
})

const onFormSubmitted = ({ values }) => {
  hubspotUserSubmittedData.email = values.email
  hubspotUserSubmittedData.firstName = values.firstname
  hubspotUserSubmittedData.lastName = values.lastname
  isFormSubmitted.value = true

  setTimeout(() => {
    isFormSubmitted.value = false
  }, 5000)
}

const movePrivacyPolicy = (form) => {
  const privacyPolicy = form.querySelector(
    '.bm-custom-form .legal-consent-container',
  )
  const actions = form.querySelector('.bm-custom-form .hs_submit .actions')

  if (!privacyPolicy || !actions) return

  actions.append(privacyPolicy)
}

const replaceSubmit = (form) => {
  const actions = form.querySelector('.bm-custom-form .hs_submit .actions')
  const submitInput = form.querySelector(
    '.bm-custom-form .hs_submit .actions input[type="submit"]',
  )

  if (!submitInput) return

  const IconCheckCircle = createElementFromString(CheckCircle)
  IconCheckCircle.classList.add('w-5', 'h-5', '[&>path]:stroke-2')
  const submitButton = createElementFromString(`
      <div
        class="flex justify-center items-center py-3 px-4 h-[46px] bg-[#4bbf7c] text-black font-semibold gap-x-1 rounded w-full xl:w-fit"
      >
        Subscribed
      </div>
`)
  submitButton.append(IconCheckCircle)

  actions.replaceChild(submitButton, submitInput)
}

const onFormReady = (form: HTMLFormElement) => {
  movePrivacyPolicy(form)

  if (!isFormSubmitted.value) return

  const inputFirstName = document.querySelector<HTMLInputElement>(
    'input[name="firstname"]',
  )
  const inputLastName = document.querySelector<HTMLInputElement>(
    'input[name="lastname"]',
  )
  const inputEmail = document.querySelector<HTMLInputElement>(
    'input[name="email"]',
  )

  inputFirstName.value = hubspotUserSubmittedData.firstName
  inputFirstName.disabled = true
  inputLastName.value = hubspotUserSubmittedData.lastName
  inputLastName.disabled = true
  inputEmail.value = hubspotUserSubmittedData.email
  inputEmail.disabled = true

  replaceSubmit(form)
}
</script>

<template>
  <section
    data-testid="newsletter-form-section"
    :class="
      twMerge(
        'newsletter-form-section relative flex flex-col items-center bg-cover px-4.5 py-9 text-white lg:px-12 lg:pb-36 lg:pt-28',
        imageExtraClasses,
      )
    "
    :style="{ backgroundImage: `url(${image})` }"
  >
    <div v-if="withOverlay" class="absolute inset-0 bg-black/50" />

    <div class="relative w-full max-w-[910px] lg:w-full">
      <h1 class="text-h4 lg:text-h3">
        {{ title }}
      </h1>
      <p class="mt-3 text-paragraph-sm">Fields marked * are required</p>
    </div>

    <div
      class="copper-newsletter-form relative mt-3 flex min-h-[300px] w-full flex-col items-end justify-center xl:min-h-20 xl:flex-row"
      :class="{ 'submitted-form-colored': isFormSubmitted }"
    >
      <HSForm
        :key="isFormSubmitted.toString()"
        :form-id="formId"
        hide-sending-loader
        @on-submitted="onFormSubmitted"
        @on-ready="onFormReady"
      >
        <template #loader="{ classes }">
          <TheLoader :class="classes" size="small" />
        </template>
      </HSForm>
    </div>
  </section>
</template>

<style lang="scss">
.copper-newsletter-form,
.copper-newsletter-form.submitted-form-colored {
  .bm-custom-form {
    width: 100%;
    .hs-form {
      gap: 0.75rem 0.5rem;

      @apply w-full h-fit grid;
      .actions {
        @apply relative;

        .legal-consent-container {
          @apply xl:absolute xl:-bottom-1/2;
        }
      }
    }

    .hs_submit {
      flex-shrink: 0;

      input[type='submit'],
      .bm-custom-form button[type='submit'] {
        height: 46px;
      }
    }

    label .hs-form-required {
      color: #fff;
    }

    .hs-error-msg {
      color: #fff !important;
    }

    .hs-form-field .input {
      .hs-input {
        background: #23252ce6;
        border: solid 1px #c1c5cf;
        &:focus {
          background: #23252ce6;
        }
      }
      .hs-input.invalid {
        border-color: #b00020;
      }
    }

    input[type='submit'] {
      color: #000;
      padding: 0.75rem 2.56rem;
    }

    @media screen and (min-width: 1024px) {
      .hs-error-msg {
        max-width: 170px;
        position: absolute;
        color: white !important;
      }
    }

    @media (min-width: 1280px) {
      .hs-form {
        grid-template-columns:
          repeat(2, minmax(0, 1fr)) minmax(0, 2fr)
          minmax(0, 140px);
        &:has(.error) {
          gap: 2.25rem 0.5rem;
        }
      }
    }

    @media screen and (max-width: 1280px) {
      .hs-error-msg {
        position: relative;
      }

      .hs_submit {
        width: 100%;
      }
    }
  }
}

@media (min-width: 768px) {
  .copper-newsletter-form,
  .copper-newsletter-form.submitted-form-colored {
    max-width: 910px;
  }
}

.copper-newsletter-form.submitted-form-colored .bm-custom-form {
  .hs-form-field .input {
    .hs-input {
      border: solid 1px #4bbf7c;
    }
  }

  input[type='submit'] {
    background: #4bbf7c;
  }
}
</style>
